<template>
  <div class="vehicle-relation-container">
    <!-- 页面标题 -->
    <div class="page-header">
      <h2>车辆关系管理</h2>
      <p class="subtitle">管理挖机与自卸车的班次关联关系</p>
    </div>

    <!-- 筛选条件 -->
    <div class="filter-section">
      <el-card class="filter-card" shadow="hover">
        <div class="filter-row">
          <el-form :model="filterForm" inline>
            <el-form-item label="班次类型">
              <el-select 
                v-model="filterForm.shiftType" 
                placeholder="请选择班次"
                clearable
                @change="handleFilterChange"
                class="filter-select"
              >
                <el-option label="早班" value="MORNING" />
                <el-option label="夜班" value="NIGHT" />
              </el-select>
            </el-form-item>
            <el-form-item label="挖机编号">
              <el-input 
                v-model="filterForm.excavatorNumber" 
                placeholder="请输入挖机编号"
                clearable
                @input="handleFilterChange"
                class="filter-input"
              />
            </el-form-item>
            <el-form-item label="自卸车编号">
              <el-input 
                v-model="filterForm.truckNumber" 
                placeholder="请输入自卸车编号"
                clearable
                @input="handleFilterChange"
                class="filter-input"
              />
            </el-form-item>
            <el-form-item>
              <el-button type="primary" @click="handleSearch" class="search-btn">
                <i class="el-icon-search"></i>
                查询
              </el-button>
              <el-button @click="handleReset" class="reset-btn">
                <i class="el-icon-refresh"></i>
                重置
              </el-button>
            </el-form-item>
          </el-form>
        </div>
      </el-card>
    </div>

    <!-- 数据表格 -->
    <div class="table-section">
      <el-card class="table-card" shadow="hover">
        <div slot="header" class="table-header">
          <div class="header-left">
            <i class="el-icon-s-data header-icon"></i>
            <span class="header-title">车辆关系列表</span>
            <el-tag type="info" size="small" class="data-count">
              共 {{ pagination.total }} 条记录
            </el-tag>
          </div>
          <div class="header-actions">
            <el-button type="success" @click="handleAdd" class="action-btn">
              <i class="el-icon-plus"></i>
              新增关系
            </el-button>
            <el-button type="primary" @click="handleBatchSubmit" :disabled="!hasChanges" class="action-btn">
              <i class="el-icon-check"></i>
              提交修改
            </el-button>
          </div>
        </div>

        <el-table 
          :data="processedTableData" 
          border 
          stripe
          v-loading="loading"
          @selection-change="handleSelectionChange"
          :span-method="objectSpanMethod"
          class="custom-table"
          :header-cell-style="headerCellStyle"
          :cell-style="cellStyle"
        >
          <el-table-column type="selection" width="55" align="center" />
          <el-table-column prop="excavatorNumber" label="挖机编号" width="120" align="center">
            <template slot-scope="scope">
              <div class="vehicle-number">
                <i class="el-icon-truck"></i>
                <span>{{ scope.row.excavatorNumber }}</span>
              </div>
            </template>
          </el-table-column>
          <el-table-column prop="excavatorName" label="挖机司机" width="150" align="center">
            <template slot-scope="scope">
              <div class="driver-info">
                <i class="el-icon-user"></i>
                <span>{{ scope.row.excavatorName || '待分配' }}</span>
              </div>
            </template>
          </el-table-column>
          <el-table-column prop="truckNumber" label="自卸车编号" width="120" align="center">
            <template slot-scope="scope">
              <div class="vehicle-number">
                <i class="el-icon-truck"></i>
                <span>{{ scope.row.truckNumber }}</span>
              </div>
            </template>
          </el-table-column>
          <el-table-column prop="truckName" label="自卸车司机" width="150" align="center">
            <template slot-scope="scope">
              <div class="driver-info">
                <i class="el-icon-user"></i>
                <span>{{ scope.row.truckName || '待分配' }}</span>
              </div>
            </template>
          </el-table-column>
          <el-table-column prop="status" label="状态" width="100" align="center">
            <template slot-scope="scope">
              <el-switch 
                v-model="scope.row.status" 
                :active-value="1" 
                :inactive-value="0"
                @change="handleDataChange"
                active-color="#67C23A"
                inactive-color="#F56C6C"
              />
            </template>
          </el-table-column>
          <el-table-column label="操作" width="200" fixed="right" align="center">
            <template slot-scope="scope">
              <div class="action-buttons">
                <el-button type="primary" size="mini" @click="handleEdit(scope.row)" class="edit-btn">
                  <i class="el-icon-edit"></i>
                  编辑
                </el-button>
                <el-button type="danger" size="mini" @click="handleDelete(scope.row)" class="delete-btn">
                  <i class="el-icon-delete"></i>
                  删除
                </el-button>
              </div>
            </template>
          </el-table-column>
        </el-table>

        <!-- 分页 -->
        <div class="pagination-wrapper">
          <el-pagination
            @size-change="handleSizeChange"
            @current-change="handleCurrentChange"
            :current-page="pagination.current"
            :page-sizes="[10, 20, 50, 100]"
            :page-size="pagination.size"
            layout="total, sizes, prev, pager, next, jumper"
            :total="pagination.total"
            background
            class="custom-pagination"
          />
        </div>
      </el-card>
    </div>

    <!-- 生效时间弹窗 -->
    <el-dialog 
      title="设置生效时间" 
      :visible.sync="effectiveTimeDialog.visible" 
      width="500px"
      :close-on-click-modal="false"
      class="custom-dialog"
    >
      <el-form :model="effectiveTimeForm" label-width="100px">
        <el-form-item label="生效时间">
          <el-date-picker
            v-model="effectiveTimeForm.effectiveTime"
            type="datetime"
            placeholder="请选择生效时间"
            format="yyyy-MM-dd HH:mm:ss"
            value-format="yyyy-MM-dd HH:mm:ss"
            :picker-options="pickerOptions"
            class="full-width"
          />
        </el-form-item>
        <el-form-item label="生效说明">
          <el-input
            v-model="effectiveTimeForm.remark"
            type="textarea"
            :rows="3"
            placeholder="请输入生效说明（可选）"
            class="full-width"
          />
        </el-form-item>
      </el-form>
      <div slot="footer" class="dialog-footer">
        <el-button @click="effectiveTimeDialog.visible = false">取消</el-button>
        <el-button type="primary" @click="handleConfirmSubmit" :loading="submitting">
          确定提交
        </el-button>
      </div>
    </el-dialog>

    <!-- 新增/编辑弹窗 -->
    <el-dialog 
      :title="editDialog.title" 
      :visible.sync="editDialog.visible" 
      width="600px"
      :close-on-click-modal="false"
      class="custom-dialog"
    >
      <el-form :model="editForm" :rules="editRules" ref="editFormRef" label-width="100px">
        <el-form-item label="挖机" prop="excavatorId">
          <el-select 
            v-model="editForm.excavatorId" 
            placeholder="请选择挖机"
            filterable
            @change="handleExcavatorChange"
            class="full-width"
          >
            <el-option 
              v-for="item in excavatorList" 
              :key="item.id" 
              :label="`${item.vehicleNumber} - ${item.name}`" 
              :value="item.id" 
            />
          </el-select>
        </el-form-item>
        <el-form-item label="自卸车" prop="truckId">
          <el-select 
            v-model="editForm.truckId" 
            placeholder="请选择自卸车"
            filterable
            multiple
            class="full-width"
          >
            <el-option 
              v-for="item in truckList" 
              :key="item.id" 
              :label="`${item.vehicleNumber} - ${item.name}`" 
              :value="item.id" 
            />
          </el-select>
        </el-form-item>
        <el-form-item label="班次类型" prop="shiftType">
          <el-select v-model="editForm.shiftType" placeholder="请选择班次" class="full-width">
            <el-option label="早班" value="MORNING" />
            <el-option label="夜班" value="NIGHT" />
          </el-select>
        </el-form-item>
        <el-form-item label="状态" prop="status">
          <el-switch v-model="editForm.status" :active-value="1" :inactive-value="0" />
        </el-form-item>
      </el-form>
      <div slot="footer" class="dialog-footer">
        <el-button @click="editDialog.visible = false">取消</el-button>
        <el-button type="primary" @click="handleSave" :loading="saving">保存</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { getList, add, edit, deleteOne, submitRelations } from '@/api/vehicleRelation'
import request from '@/utils/request'

export default {
  name: 'VehicleRelationManagement',
  data() {
    return {
      loading: false,
      saving: false,
      submitting: false,
      hasChanges: false,

      // 筛选表单
      filterForm: {
        shiftType: 'MORNING', 
        excavatorNumber: '',
        truckNumber: ''
      },

      // 分页
      pagination: {
        current: 1,
        size: 20,
        total: 0
      },

      // 表格数据
      tableData: [],
      selectedRows: [],

      // 车辆列表
      excavatorList: [],
      truckList: [],

      // 生效时间弹窗
      effectiveTimeDialog: {
        visible: false
      },

      effectiveTimeForm: {
        effectiveTime: '',
        remark: ''
      },

      // 编辑弹窗
      editDialog: {
        visible: false,
        title: '新增车辆关系'
      },

      editForm: {
        id: null,
        excavatorId: null,
        truckId: [],
        shiftType: '',
        sortOrder: 0,
        status: 1
      },

      editRules: {
        excavatorId: [{ required: true, message: '请选择挖机', trigger: 'change' }],
        truckId: [{ required: true, message: '请选择自卸车', trigger: 'change' }],
        shiftType: [{ required: true, message: '请选择班次', trigger: 'change' }]
      },

      // 日期选择器配置
      pickerOptions: {
        disabledDate(time) {
          return time.getTime() < Date.now() - 8.64e7
        }
      },

      // 合并单元格数据
      spanData: []
    }
  },

  computed: {
    // 处理后的表格数据，用于合并单元格
    processedTableData() {
      return this.tableData
    }
  },

  mounted() {
    this.loadData()
    this.loadVehicleList()
  },

  methods: {
    // 表格头部样式
    headerCellStyle() {
      return {
        backgroundColor: '#f8f9fa',
        color: '#495057',
        fontWeight: '600',
        fontSize: '14px',
        borderBottom: '2px solid #dee2e6'
      }
    },

    // 表格单元格样式
    cellStyle() {
      return {
        fontSize: '13px',
        padding: '12px 0'
      }
    },

    async loadData() {
      this.loading = true
      try {
        const response = await getList(
          { current: this.pagination.current, size: this.pagination.size },
          this.filterForm
        )
        if (response.code === 200) {
          this.tableData = response.data.records || []
          this.pagination.total = response.data.total || 0
          this.calculateSpanData()
        } else {
          this.$message.error(response.msg || '加载数据失败')
        }
      } catch (error) {
        console.error('加载数据失败:', error)
        this.$message.error('加载数据失败')
      } finally {
        this.loading = false
      }
    },

    // 计算合并单元格数据
    calculateSpanData() {
      this.spanData = []
      let pos = 0
      let excavatorCount = 1
      
      for (let i = 0; i < this.tableData.length; i++) {
        if (i === 0) {
          this.spanData.push(1)
          pos = 0
          continue
        }
        
        // 如果当前行与上一行的挖机编号相同，则合并
        if (this.tableData[i].excavatorNumber === this.tableData[i - 1].excavatorNumber) {
          this.spanData[pos] += 1
          this.spanData.push(0)
        } else {
          this.spanData.push(1)
          pos = i
        }
      }
    },

    // 合并单元格方法
    objectSpanMethod({ row, column, rowIndex, columnIndex }) {
      if (columnIndex === 1 || columnIndex === 2) { // 挖机编号和挖机名称列
        const _row = this.spanData[rowIndex]
        const _col = _row > 0 ? 1 : 0
        return {
          rowspan: _row,
          colspan: _col
        }
      }
    },

    async loadVehicleList() {
      try {
        const excavatorResponse = await request({
          url: '/admin/vehicle/list',
          method: 'get',
          params: { 
            vehicleType: 'EXCAVATOR'
          }
        })
        
        if (excavatorResponse.code === 200) {
          this.excavatorList = excavatorResponse.data || []
        }
        
        const truckResponse = await request({
          url: '/admin/vehicle/list',
          method: 'get',
          params: { 
            vehicleType: 'DUMP_TRUCK'
          }
        })
        
        if (truckResponse.code === 200) {
          this.truckList = truckResponse.data || []
        }
      } catch (error) {
        console.error('加载车辆列表失败:', error)
        this.$message.error('加载车辆列表失败')
      }
    },

    handleFilterChange() {
      this.pagination.current = 1
      this.loadData()
    },

    handleSearch() {
      this.pagination.current = 1
      this.loadData()
    },

    handleReset() {
      this.filterForm = {
        shiftType: 'MORNING',
        excavatorNumber: '',
        truckNumber: ''
      }
      this.pagination.current = 1
      this.loadData()
    },

    handleSizeChange(size) {
      this.pagination.size = size
      this.loadData()
    },

    handleCurrentChange(current) {
      this.pagination.current = current
      this.loadData()
    },

    handleSelectionChange(selection) {
      this.selectedRows = selection
    },

    handleDataChange() {
      this.hasChanges = true
    },

    handleAdd() {
      this.editDialog.title = '新增车辆关系'
      this.editForm = {
        id: null,
        excavatorId: null,
        truckId: [],
        shiftType: '',
        sortOrder: 0,
        status: 1
      }
      this.editDialog.visible = true
    },

    handleEdit(row) {
      this.editDialog.title = '编辑车辆关系'
      this.editForm = {
        id: row.id,
        excavatorId: row.excavatorId,
        truckId: [row.truckId], // 编辑时只显示单个自卸车
        shiftType: row.shiftType,
        sortOrder: row.sortOrder,
        status: row.status
      }
      this.editDialog.visible = true
    },

    async handleDelete(row) {
      try {
        await this.$confirm('确定要删除这条关系记录吗？', '提示', {
          type: 'warning'
        })
        
        const response = await deleteOne(row.id)
        if (response.code === 200) {
          this.$message.success('删除成功')
          this.loadData()
        } else {
          this.$message.error(response.msg || '删除失败')
        }
      } catch (error) {
        if (error !== 'cancel') {
          console.error('删除失败:', error)
          this.$message.error('删除失败')
        }
      }
    },

    async handleSave() {
      try {
        await this.$refs.editFormRef.validate()
        this.saving = true
        
        let response
        if (this.editDialog.title === '新增车辆关系') {
          response = await add(this.editForm)
        } else {
          response = await edit(this.editForm)
        }
        
        if (response.code === 200) {
          this.$message.success('保存成功')
          this.editDialog.visible = false
          this.loadData()
        } else {
          this.$message.error(response.msg || '保存失败')
        }
      } catch (error) {
        console.error('保存失败:', error)
        this.$message.error('保存失败')
      } finally {
        this.saving = false
      }
    },

    handleBatchSubmit() {
      this.effectiveTimeForm.effectiveTime = ''
      this.effectiveTimeForm.remark = ''
      this.effectiveTimeDialog.visible = true
    },

    async handleConfirmSubmit() {
      if (!this.effectiveTimeForm.effectiveTime) {
        this.$message.warning('请选择生效时间')
        return
      }
      
      try {
        this.submitting = true
        
        const response = await submitRelations({
          effectiveTime: this.effectiveTimeForm.effectiveTime,
          remark: this.effectiveTimeForm.remark,
          relations: this.tableData
        })
        
        if (response.code === 200) {
          this.$message.success('提交成功')
          this.effectiveTimeDialog.visible = false
          this.hasChanges = false
          this.loadData()
        } else {
          this.$message.error(response.msg || '提交失败')
        }
      } catch (error) {
        console.error('提交失败:', error)
        this.$message.error('提交失败')
      } finally {
        this.submitting = false
      }
    },

    handleExcavatorChange() {
      this.editForm.truckId = []
    }
  }
}
</script>

<style scoped>
.vehicle-relation-container {
  padding: 20px;
  background-color: #f5f7fa;
  min-height: 100vh;
}

.page-header {
  margin-bottom: 20px;
  text-align: center;
}

.page-header h2 {
  color: #303133;
  margin: 0 0 8px 0;
  font-size: 24px;
  font-weight: 600;
}

.subtitle {
  color: #909399;
  margin: 0;
  font-size: 14px;
}

.filter-section {
  margin-bottom: 20px;
}

.filter-card {
  border-radius: 12px;
  border: none;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
}

.filter-card >>> .el-card__body {
  padding: 20px;
}

.filter-row {
  display: flex;
  align-items: center;
}

/* 修改筛选区域标签文字颜色为白色 */
.filter-card >>> .el-form-item__label {
  color: #ffffff !important;
  font-weight: 500;
  font-size: 14px;
}

/* 修改输入框和选择框的样式 */
.filter-select,
.filter-input {
  width: 200px;
}

/* 输入框内部文字颜色 */
.filter-card >>> .el-input__inner {
  color: #333;
  background-color: rgba(255, 255, 255, 0.9);
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: 6px;
}

.filter-card >>> .el-input__inner:focus {
  border-color: #ffffff;
  box-shadow: 0 0 0 2px rgba(255, 255, 255, 0.2);
}

/* 选择框样式 */
.filter-card >>> .el-select .el-input__inner {
  color: #333;
  background-color: rgba(255, 255, 255, 0.9);
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: 6px;
}

.filter-card >>> .el-select .el-input__inner:focus {
  border-color: #ffffff;
  box-shadow: 0 0 0 2px rgba(255, 255, 255, 0.2);
}

/* 选择框下拉箭头颜色 */
.filter-card >>> .el-select .el-input__suffix {
  color: #333;
}

/* 选择框下拉菜单样式 */
.filter-card >>> .el-select-dropdown {
  background-color: #fff;
  border: 1px solid #e4e7ed;
  border-radius: 6px;
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
}

/* 选择框选项样式 */
.filter-card >>> .el-select-dropdown__item {
  color: #333;
}

.filter-card >>> .el-select-dropdown__item:hover {
  background-color: #f5f7fa;
  color: #667eea;
}

.filter-card >>> .el-select-dropdown__item.selected {
  background-color: #667eea;
  color: #fff;
}

.search-btn,
.reset-btn {
  border-radius: 8px;
  padding: 10px 20px;
  font-weight: 500;
}

.search-btn {
  background: rgba(255, 255, 255, 0.2);
  border: 1px solid rgba(255, 255, 255, 0.3);
  color: #ffffff;
  backdrop-filter: blur(10px);
}

.search-btn:hover {
  background: rgba(255, 255, 255, 0.3);
  border-color: rgba(255, 255, 255, 0.5);
  color: #ffffff;
}

.reset-btn {
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.3);
  color: #ffffff;
  backdrop-filter: blur(10px);
}

.reset-btn:hover {
  background: rgba(255, 255, 255, 0.2);
  border-color: rgba(255, 255, 255, 0.5);
  color: #ffffff;
}

.table-section {
  margin-bottom: 20px;
}

.table-card {
  border-radius: 12px;
  border: none;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
}

.table-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 12px;
}

.header-icon {
  font-size: 20px;
  color: #667eea;
}

.header-title {
  font-weight: 600;
  color: #303133;
  font-size: 16px;
}

.data-count {
  background: #e3f2fd;
  color: #1976d2;
  border: none;
}

.header-actions {
  display: flex;
  gap: 12px;
}

.action-btn {
  border-radius: 8px;
  padding: 8px 16px;
  font-weight: 500;
  transition: all 0.3s ease;
}

.action-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.custom-table {
  border-radius: 8px;
  overflow: hidden;
}

.custom-table >>> .el-table__header-wrapper {
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
}

.custom-table >>> .el-table__body-wrapper {
  background: #fff;
}

.vehicle-number {
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 500;
  color: #2c3e50;
}

.vehicle-number i {
  color: #667eea;
  font-size: 16px;
}

.driver-info {
  display: flex;
  align-items: center;
  gap: 8px;
  color: #6c757d;
}

.driver-info i {
  color: #28a745;
  font-size: 14px;
}

.action-buttons {
  display: flex;
  gap: 8px;
  justify-content: center;
}

.edit-btn,
.delete-btn {
  border-radius: 6px;
  padding: 6px 12px;
  font-size: 12px;
  transition: all 0.3s ease;
}

.edit-btn:hover {
  background: #409eff;
  border-color: #409eff;
  transform: scale(1.05);
}

.delete-btn:hover {
  background: #f56c6c;
  border-color: #f56c6c;
  transform: scale(1.05);
}

.pagination-wrapper {
  margin-top: 20px;
  display: flex;
  justify-content: center;
}

.custom-pagination >>> .el-pagination.is-background .el-pager li:not(.disabled).active {
  background-color: #667eea;
  color: #fff;
}

.custom-pagination >>> .el-pagination.is-background .el-pager li:not(.disabled):hover {
  color: #667eea;
}

.dialog-footer {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
}

.custom-dialog >>> .el-dialog {
  border-radius: 12px;
  overflow: hidden;
}

.custom-dialog >>> .el-dialog__header {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: #fff;
  padding: 20px;
}

.custom-dialog >>> .el-dialog__title {
  color: #fff;
  font-weight: 600;
}

.custom-dialog >>> .el-dialog__body {
  padding: 30px;
}

.full-width {
  width: 100%;
}

/* 响应式设计 */
@media (max-width: 768px) {
  .vehicle-relation-container {
    padding: 10px;
  }
  
  .filter-row {
    flex-direction: column;
    gap: 10px;
  }
  
  .filter-select,
  .filter-input {
    width: 100%;
  }
  
  .header-actions {
    flex-direction: column;
    gap: 8px;
  }
  
  .action-buttons {
    flex-direction: column;
    gap: 4px;
  }
}
</style>